// src/lib/horaires.js — helper PARTAGÉ de normalisation + résolution des horaires
// check-in / check-out d'un logement (cfg KV `fbz8-*`).
//
// Contexte : les heures stockées en cfg sont au format SALE / hétérogène —
// « 17h », « 9h30 », « 15:00:00 », « 15:00 », vide… Plusieurs consommateurs
// (api/sites.js buildSitePayload, api/form-config.js) doivent les exposer en
// HH:MM strict et appliquer la même cascade de résolution (LOT E, field-first
// 2026-06-24) :
//   flat checkin_time/checkout_time (Instructions = vérité ADR-199)
//     → schedule.* (legacy) → défaut 16:00 / 10:00.
//
// ADR-197 (LOT 1) : le fallback parentLog (sous-loc Maison Zen) a été retiré —
// les chambres lc1/lc2/lc3 ont leur propre schedule checkin/checkout seedé
// (LOT 0). Lecture self uniquement.
//
// ⚠️ SOURCE UNIQUE pour le serveur ET l'écran (F10 phase 2, 03/10). Écrit en
// CommonJS pur, zéro dépendance : l'écran (LivretTab, copy-to-chambres)
// l'importe directement ; le serveur le lit par le relais `lib/horaires.js`.
// Ne rien ajouter qui ne soit chargeable des deux côtés (garde :
// src/lib/doublons-lib-conformite.test.js, « sources uniques »).

// Défaut unique 16:00 / 10:00 (décision 2026-06-15, aligné resolveLogTime).
const DEFAULT_CHECKIN = '16:00';
const DEFAULT_CHECKOUT = '10:00';

/**
 * Normalise une heure « sale » vers HH:MM strict (24h, zéro-paddé).
 * Accepte : « 17h », « 9h30 », « 9h », « 15:00:00 », « 15:00 », « 15h00 »,
 * « 8 », nombre 8… Retourne '' si non parsable.
 *
 * @param {string|number|null|undefined} raw
 * @returns {string} 'HH:MM' ou ''
 */
function normalizeHour(raw) {
  if (raw == null) return '';
  const s = String(raw).trim().toLowerCase();
  if (!s) return '';

  // Formats avec séparateur ':' ou 'h' (ex. "15:00:00", "9h30", "15h00", "17h").
  // 1) heures:minutes éventuelles : capture H et M optionnel.
  const m = s.match(/^(\d{1,2})\s*[:hH]\s*(\d{0,2})/);
  if (m) {
    const hh = parseInt(m[1], 10);
    const mm = m[2] === '' || m[2] == null ? 0 : parseInt(m[2], 10);
    if (!Number.isFinite(hh) || hh < 0 || hh > 23) return '';
    if (!Number.isFinite(mm) || mm < 0 || mm > 59) return '';
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }

  // 2) entier nu (« 8 », « 15 ») → heure pleine.
  const onlyDigits = s.match(/^(\d{1,2})$/);
  if (onlyDigits) {
    const hh = parseInt(onlyDigits[1], 10);
    if (hh < 0 || hh > 23) return '';
    return `${String(hh).padStart(2, '0')}:00`;
  }

  return '';
}

/**
 * Résout les heures check-in / check-out effectives d'un logement déjà résolu
 * en cfg. Cascade : self → défaut 15:00 / 11:00.
 *
 * ADR-197 (LOT 1) : le fallback parentLog a été retiré (chambres seedées).
 *
 * Lit les deux conventions de champ rencontrées en cfg :
 *   - `checkin_time` / `checkout_time` (convention sites.js)
 *   - `schedule.checkin_start` / `schedule.checkout_time` (convention form/schedule)
 * et tolère `checkin` / `checkout` legacy.
 *
 * @param {object|null} logFromCfg — logement résolu (flattenLogs)
 * @returns {{ checkin: string, checkout: string }} HH:MM strict, jamais vide (défauts appliqués)
 */
function resolveCheckinCheckout(logFromCfg) {
  const log = logFromCfg || {};

  // LOT E (field-first, 2026-06-24) : le flat `checkin_time`/`checkout_time`
  // (onglet Instructions = SOURCE DE VÉRITÉ ADR-199) est PRIORITAIRE ; le
  // `schedule.*` (résidu hérité, ex. checkin_start « 16:00 ») n'est plus qu'un
  // fallback de rétrocompat. Aligné sur resolveLogTime (lib/template-variables.js)
  // et resolveHour (lib/livret-derive.js) → cohérence 17h tous canaux.
  // « Le vide ne gagne pas » : un champ flat vide/blanc retombe sur schedule.
  const pickCheckin = (o) => {
    if (!o) return '';
    const sched = o.schedule || {};
    return (
      normalizeHour(o.checkin_time)
        || normalizeHour(o.checkin)
        || normalizeHour(sched.checkin_start)
        || normalizeHour(sched.checkin)
    );
  };
  const pickCheckout = (o) => {
    if (!o) return '';
    const sched = o.schedule || {};
    return (
      normalizeHour(o.checkout_time)
        || normalizeHour(o.checkout)
        || normalizeHour(sched.checkout_time)
    );
  };

  const checkin = pickCheckin(log) || DEFAULT_CHECKIN;
  const checkout = pickCheckout(log) || DEFAULT_CHECKOUT;
  return { checkin, checkout };
}

/**
 * L'heure d'arrivée CONFIGURÉE — ou null si aucune ne l'est (2026-08-21 :
 * « un logement sans heure de contrat doit le dire, pas inventer une heure »).
 * Même cascade que resolveCheckinCheckout, SANS le défaut.
 */
function checkinConfigure(logFromCfg) {
  const log = logFromCfg || {};
  const sched = log.schedule || {};
  return (
    normalizeHour(log.checkin_time)
      || normalizeHour(log.checkin)
      || normalizeHour(sched.checkin_start)
      || normalizeHour(sched.checkin)
      || null
  );
}

/**
 * Cascade field-first SANS défaut : renvoie '' quand le logement ne porte
 * AUCUNE heure. Utile là où « rien de saisi » doit rester distinct de « valeur
 * par défaut » — typiquement la copie vers les chambres sœurs, qui ne doit
 * jamais propager un défaut comme s'il s'agissait d'une saisie (sinon copier
 * une chambre sans check-out écraserait le check-out réel de sa sœur).
 *
 * Venu de l'ancienne copie écran (copy-to-chambres) ; même cascade que
 * resolveCheckinCheckout ci-dessus, sans le défaut.
 *
 * @param {object|null} logFromCfg
 * @returns {{ checkin: string, checkout: string }} HH:MM strict ou ''
 */
function resolveCheckinCheckoutRaw(logFromCfg) {
  const o = logFromCfg || {};
  const sched = o.schedule || {};
  const checkin = (
    normalizeHour(o.checkin_time)
      || normalizeHour(o.checkin)
      || normalizeHour(sched.checkin_start)
      || normalizeHour(sched.checkin)
  );
  const checkout = (
    normalizeHour(o.checkout_time)
      || normalizeHour(o.checkout)
      || normalizeHour(sched.checkout_time)
  );
  return { checkin, checkout };
}

module.exports = { normalizeHour, resolveCheckinCheckout, resolveCheckinCheckoutRaw, checkinConfigure, DEFAULT_CHECKIN, DEFAULT_CHECKOUT };
